<template>
  <div class="table-responsive">
    <div class="toolbar-actions">
      <div class="toolbar-actions__start">
        <button type="button" class="action-btn action-btn--info mb-0" @click="$emit('open-dialog')">
          <i class="bi bi-person-plus" aria-hidden="true"></i>
          <span>{{ $t("requestNewInsured") }}</span>
        </button>
        <button type="button" class="action-btn action-btn--excel mb-0" @click="$emit('open-dialog-excel')">
          <i class="bi bi-file-earmark-excel" aria-hidden="true"></i>
          <span>{{ $t("commonImport") }}</span>
        </button>
      </div>
      <div class="toolbar-actions__end ms-auto">
        <button type="button" class="action-btn action-btn--info mb-0" @click="$emit('open-dialog-update-info')">
          <i class="bi bi-pencil-square" aria-hidden="true"></i>
          <span>{{ $t("insuredUpdateInfo") }}</span>
        </button>
        <button type="button" class="action-btn action-btn--warning mb-0" @click="$emit('open-dialog-program-change')">
          <i class="bi bi-arrow-left-right" aria-hidden="true"></i>
          <span>{{ $t("insuredProgramChange") }}</span>
        </button>
        <button type="button" class="action-btn action-btn--danger mb-0" @click="$emit('open-dialog-terminate')">
          <i class="bi bi-x-circle" aria-hidden="true"></i>
          <span>{{ $t("insuredTerminate") }}</span>
        </button>
      </div>
    </div>

    <el-table size="small" border class="el-table-css" :data="dataTable" style="width: 100%;">
      <el-table-column fixed="left" width="50" align="center">
        <template #default="{ row }">
          <button
            type="button"
            class="table-icon-btn table-icon-btn--info"
            :title="$t('insuredChanges')"
            :aria-label="$t('insuredChanges')"
            @click="openBulkItems(row)"
          >
            <i class="bi bi-info-circle" aria-hidden="true"></i>
          </button>
        </template>
      </el-table-column>
      <el-table-column prop="bulkId" label="#" width="70" align="center">
        <template #default="{ row }">
          <span class="fw-bold">#{{ row.bulkId }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="requestDateStr" :label="$t('commonDate')" width="150" align="center">
        <template #default="{ row }">
          <span>{{ row.requestDateStr || row.requestDate }}</span>
        </template>
      </el-table-column>
      <el-table-column prop="operTypeName" :label="$t('requestOperationType')" min-width="170">
        <template #default="{ row }">
          <span class="badge" :class="operTypeBadgeClass(row.operType)">
            {{ row.operTypeName || resolveOperTypeName(row.operType) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('bulkInsuredCount')" width="130" align="center">
        <template #default="{ row }">
          <span class="badge badge--count cursor-pointer" :title="$t('insuredChanges')" @click="openBulkItems(row)">
            <i class="bi bi-people-fill me-1"></i>{{ row.totalItems }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="groupName" :label="$t('commonProgram')" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">
          <span>{{ row.groupName || "-" }}</span>
        </template>
      </el-table-column>
      <el-table-column :label="$t('commonStatus')" width="120" align="center">
        <template #default="{ row }">
          <span class="badge" :class="statusBadgeClass(row.requestStatus)">
            {{ row.requestStatusName || statusName(row.requestStatus) }}
          </span>
        </template>
      </el-table-column>
      <el-table-column prop="requestUserName" :label="$t('commonCreated')" width="160" show-overflow-tooltip />
      <el-table-column prop="descr" :label="$t('commonNotes')" min-width="160" show-overflow-tooltip />
      <el-table-column fixed="right" width="60" align="center">
        <template #default="{ row }">
          <button
            type="button"
            class="table-icon-btn table-icon-btn--danger"
            :title="$t('commonDelete') || 'Müraciəti sil'"
            :aria-label="$t('commonDelete') || 'Müraciəti sil'"
            @click="handleDeleteBulk(row)"
          >
            <i class="bi bi-trash3" aria-hidden="true"></i>
          </button>
        </template>
      </el-table-column>
    </el-table>
  </div>

  <div class="table-pagination">
    <el-pagination
      :current-page="currentPage"
      :page-size="maxRow"
      background
      layout="prev, pager, next"
      :total="totalItems"
      @current-change="handleCurrentChange"
    />
  </div>

  <!-- Bulk items drilldown dialog -->
  <el-dialog
    v-model="bulkItemsDialogVisible"
    :title="selectedBulk ? `${$t('bulkChangesDetails')} (#${selectedBulk.bulkId} - ${selectedBulk.operTypeName || ''})` : $t('bulkChangesDetails')"
    width="88%"
    top="5vh"
    destroy-on-close
    append-to-body
  >
    <req-bulk-items-dialog
      :bulk-info="selectedBulk"
      @deleted="onBulkDeleted"
      @close="bulkItemsDialogVisible = false"
    />
  </el-dialog>
</template>

<script>
import ReqBulkItemsDialog from "./ReqBulkItemsDialog.vue";
import Source from "@/project/Source/claim.js";
import { swalDialogClass } from "@/project/Source/helper.js";

export default {
  name: "ReqBulkTable",
  components: {
    ReqBulkItemsDialog,
  },
  props: {
    maxRow: {
      type: Number,
      default: 10,
    },
    requestsCount: {
      type: Number,
      default: 0,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    dataTable: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    "get-table",
    "open-dialog",
    "open-dialog-excel",
    "open-dialog-update-info",
    "open-dialog-program-change",
    "open-dialog-terminate",
  ],
  data() {
    return {
      totalItems: this.requestsCount,
      bulkItemsDialogVisible: false,
      selectedBulk: null,
    };
  },
  watch: {
    requestsCount(val) {
      this.totalItems = val;
    },
  },
  methods: {
    handleCurrentChange(val) {
      this.$emit("get-table", val);
    },
    openBulkItems(row) {
      this.selectedBulk = row;
      this.bulkItemsDialogVisible = true;
    },
    onBulkDeleted() {
      this.bulkItemsDialogVisible = false;
      this.$emit("get-table", this.currentPage);
    },
    handleDeleteBulk(row) {
      const operName = row.operTypeName || this.resolveOperTypeName(row.operType);
      this.$swal({
        title: this.$t("commonWarning") || "Diqqət",
        text: `#${row.bulkId} nömrəli "${operName}" müraciətini silmək istədiyinizdən əminsiniz? Daxil edilmiş bütün (${row.totalItems}) qeydlər silinəcək.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("commonDelete") || "Bəli, sil",
        cancelButtonText: this.$t("commonCancel") || "İmtina",
        customClass: swalDialogClass(),
        buttonsStyling: false,
      }).then((result) => {
        if (result.isConfirmed) {
          Source.AccountRequestBulkDelete({ id: row.bulkId, requestCorrelationId: "string" })
            .then((res) => {
              if (res.data?.isSuccess) {
                this.$swal({
                  title: this.$t("commonSuccess") || "Uğurlu",
                  text: `#${row.bulkId} nömrəli müraciət uğurla silindi.`,
                  icon: "success",
                  timer: 1800,
                  showConfirmButton: false,
                  customClass: swalDialogClass(),
                });
                this.$emit("get-table", this.currentPage);
              } else {
                this.$swal({
                  title: this.$t("commonError") || "Xəta",
                  text: (res.data?.errors || []).join(", ") || "Müraciəti silmək mümkün olmadı.",
                  icon: "error",
                  customClass: swalDialogClass(),
                });
              }
            })
            .catch((err) => {
              this.$swal({
                title: this.$t("commonError") || "Xəta",
                text: err?.message || "Müraciəti silmək mümkün olmadı.",
                icon: "error",
                customClass: swalDialogClass(),
              });
            });
        }
      });
    },
    operTypeBadgeClass(type) {
      if (type === 1) return "badge--info";
      if (type === 2) return "badge--danger";
      if (type === 3) return "badge--warning";
      if (type === 4) return "badge--purple";
      return "badge--muted";
    },
    resolveOperTypeName(type) {
      if (type === 1) return this.$t("requestNewInsured") || "Yeni sığortalı";
      if (type === 2) return this.$t("insuredTerminate") || "Xitam";
      if (type === 3) return this.$t("insuredProgramChange") || "Qrup dəyişikliyi";
      if (type === 4) return this.$t("insuredUpdateInfo") || "Məlumat dəyişikliyi";
      return "Digər";
    },
    statusBadgeClass(status) {
      if (status === 0) return "badge--warning";
      if (status === 1) return "badge--success";
      if (status === 2) return "badge--danger";
      return "badge--muted";
    },
    statusName(status) {
      if (status === 0) return this.$t("commonNew");
      if (status === 1) return this.$t("commonApproved");
      if (status === 2) return this.$t("commonRejected");
      return "";
    },
  },
};
</script>

<style scoped>
.badge--info {
  background: color-mix(in srgb, var(--bs-info) 18%, transparent);
  color: var(--bs-info);
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
}
.badge--warning {
  background: rgba(245, 158, 11, 0.15);
  color: #d97706;
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
}
.badge--purple {
  background: rgba(139, 92, 246, 0.15);
  color: #7c3aed;
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
}
.badge--success {
  background: rgba(16, 185, 129, 0.15);
  color: #059669;
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
}
.badge--danger {
  background: rgba(220, 38, 38, 0.15);
  color: #dc2626;
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
}
.badge--muted {
  background: rgba(120, 130, 145, 0.15);
  color: var(--app-text);
  padding: 0.35rem 0.6rem;
  border-radius: 6px;
  font-weight: 600;
}
.badge--count {
  background: color-mix(in srgb, var(--bs-primary, #6366f1) 15%, transparent);
  color: var(--bs-primary, #6366f1);
  padding: 0.35rem 0.65rem;
  border-radius: 20px;
  font-weight: 700;
  transition: all 0.2s ease;
}
.badge--count:hover {
  background: var(--bs-primary, #6366f1);
  color: #fff;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
